<template>
  <div class="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2">
    <!-- Left container - Rating buttons -->
    <div class="flex items-center gap-1">
      <button
        :class="[
          'rounded-md p-1.5 transition-colors',
          'text-gray-400 hover:bg-purple-50 hover:text-purple-600',
          { 'bg-purple-50 text-purple-600': rating === 'positive' }
        ]"
        :title="rating === 'positive' ? t('MarkedHelpful') : t('Helpful')"
        @click="handleRating('positive')"
      >
        <ThumbsUp class="h-5 w-5" />
      </button>
      <button
        :class="[
          'rounded-md p-1.5 transition-colors',
          'text-gray-400 hover:bg-purple-50 hover:text-purple-600',
          { 'bg-purple-50 text-purple-600': rating === 'negative' }
        ]"
        :title="rating === 'negative' ? t('MarkedNotHelpful') : t('NotHelpful')"
        @click="handleRating('negative')"
      >
        <ThumbsDown class="h-5 w-5" />
      </button>
    </div>

    <!-- Right container - Action buttons -->
    <div class="flex items-center gap-1">
      <!-- Save Snippet Button -->
      <button
        :class="[
          'rounded-md p-1.5 transition-colors',
          'text-gray-400 hover:bg-purple-50 hover:text-purple-600'
        ]"
        :title="responseSaved ? t('ResponseSaved') : t('SaveResponse')"
        data-cy="save-snippet-button"
        @click="handleSaveSnippet"
      >
        <div class="flex items-center gap-1">
          <transition
            name="fade"
            mode="out-in"
          >
            <BookmarkPlus
              v-if="!responseSaved"
              key="bookmark-icon"
              class="h-5 w-5"
            />
            <BookmarkCheck
              v-else
              key="bookmark-check-icon"
              class="h-5 w-5 text-purple-600"
            />
          </transition>
          <span class="text-sm text-gray-500">
            {{ responseSaved ? t('Saved') : t('SaveResponse') }}
          </span>
        </div>
      </button>

      <!-- Download Button -->
      <button
        :class="[
          'rounded-md p-1.5 transition-colors',
          'text-gray-400 hover:bg-purple-50 hover:text-purple-600'
        ]"
        :title="t('Download')"
        @click="handleDownload"
      >
        <div class="flex items-center gap-1">
          <Download class="h-5 w-5" />
          <span class="text-sm text-gray-500">{{ t('Download') }}</span>
        </div>
      </button>

      <!-- Copy Button -->
      <button
        :class="[
          'rounded-md p-1.5 transition-colors',
          'text-gray-400 hover:bg-purple-50 hover:text-purple-600'
        ]"
        :title="copyButtonTitle"
        @click="copyToClipboard"
      >
        <div class="flex items-center gap-1">
          <transition
            name="fade"
            mode="out-in"
          >
            <Copy
              v-if="!copied"
              key="copy-icon"
              class="h-5 w-5"
            />
            <Check
              v-else
              key="check-icon"
              class="h-5 w-5 text-purple-600"
            />
          </transition>
          <span class="text-sm text-gray-500">{{ t('Copy') }}</span>
        </div>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useTranslation } from '@/composables/useTranslation'
  import { type AIChatSession, type AIChatSessionMessage, FeatureEnum } from '@/types/generated'
  import { FeedbackService } from '@/types/generated/services.gen'
  import {
    BookmarkCheck,
    BookmarkPlus,
    Check,
    Copy,
    Download,
    ThumbsDown,
    ThumbsUp
  } from 'lucide-vue-next'
  import { computed, inject, ref, type Ref } from 'vue'

  const props = defineProps<{
    message: AIChatSessionMessage
  }>()

  const emit = defineEmits<{
    'save-snippet': []
    rated: [{ value: 'positive' | 'negative'; feedback_uuid?: string }]
  }>()

  const { t } = useTranslation({
    en: {
      Helpful: 'Helpful',
      NotHelpful: 'Not helpful',
      MarkedHelpful: 'Marked as helpful',
      MarkedNotHelpful: 'Marked as not helpful',
      Download: 'Download',
      Copy: 'Copy',
      CopyToClipboard: 'Copy to clipboard',
      Copied: 'Copied!',
      SaveResponse: 'Save snippet',
      ResponseSaved: 'Snippet saved',
      Saved: 'Saved'
    },
    de: {
      Helpful: 'Hilfreich',
      NotHelpful: 'Nicht hilfreich',
      MarkedHelpful: 'Als hilfreich markiert',
      MarkedNotHelpful: 'Als nicht hilfreich markiert',
      Download: 'Herunterladen',
      Copy: 'Kopieren',
      CopyToClipboard: 'In Zwischenablage kopieren',
      Copied: 'Kopiert!',
      SaveResponse: 'Snippet speichern',
      ResponseSaved: 'Snippet gespeichert',
      Saved: 'Gespeichert'
    }
  })

  const rating = ref<'positive' | 'negative' | null>(null)
  const copied = ref(false)
  const responseSaved = ref(false)
  const copyButtonTitle = computed(() => (copied.value ? t('Copied') : t('CopyToClipboard')))

  const activeChatSession = inject<Ref<AIChatSession | null>>('activeChatSession', ref(null))

  const handleRating = async (value: 'positive' | 'negative') => {
    if (rating.value === value) {
      rating.value = null
      return
    }

    rating.value = value

    try {
      const response = await FeedbackService.feedbackCreate({
        requestBody: {
          rating: value === 'positive' ? '5.0' : '0.0',
          feature: FeatureEnum.AI_CHAT,
          url: window.location.href,
          related_data: {
            ai_chat: {
              ai_chat_session_uuid: activeChatSession.value?.uuid,
              ai_chat_session_message_uuid: props.message.uuid
            }
          }
        }
      })

      // Emit the rating event with feedback UUID
      emit('rated', {
        value,
        feedback_uuid: response.uuid
      })
    } catch (error) {
      console.error('Failed to submit rating:', error)
      // Reset rating on error
      rating.value = null
    }
  }

  const handleSaveSnippet = () => {
    emit('save-snippet')
    responseSaved.value = true

    // Reset the saved state after a delay
    setTimeout(() => {
      responseSaved.value = false
    }, 2000)
  }

  const handleDownload = () => {
    // Extract text content from message
    let textContent = ''

    if (typeof props.message.content === 'string') {
      textContent = props.message.content
    } else if (props.message.content?.chunks) {
      const chunks = Array.isArray(props.message.content.chunks)
        ? (props.message.content.chunks as any[])
        : []

      textContent = chunks
        .filter((chunk) => chunk?.type === 'text')
        .map((chunk) => chunk.text || '')
        .join('')
    }

    // Create and download file
    const blob = new Blob([textContent], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `ai-response-${new Date().toISOString().slice(0, 10)}.txt`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const copyToClipboard = async () => {
    try {
      // Extract text content from message
      let textContent = ''

      if (typeof props.message.content === 'string') {
        textContent = props.message.content
      } else if (props.message.content?.chunks) {
        const chunks = Array.isArray(props.message.content.chunks)
          ? (props.message.content.chunks as any[])
          : []

        textContent = chunks
          .filter((chunk) => chunk?.type === 'text')
          .map((chunk) => chunk.text || '')
          .join('')
      }

      await navigator.clipboard.writeText(textContent)
      copied.value = true
      setTimeout(() => {
        copied.value = false
      }, 2000)
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
    }
  }
</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
